import { PropsWithChildren, RefCallback, useCallback, useLayoutEffect, useState } from "react";

import Box from "@mui/material/Box";

import { DashboardTooltipProps } from "@/dashboard-design-system/DashboardTooltip";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardTooltip";

interface TruncatedTextWithTooltipProps {
  tooltipProps: Pick<DashboardTooltipProps, "title" | "placement" | "data-test" | "PopperProps">;
  "data-test"?: string;
}

export const TruncatedTextWithTooltip = ({
  children,
  tooltipProps,
  "data-test": dataTest,
}: PropsWithChildren<TruncatedTextWithTooltipProps>) => {
  const { title, ...restTooltipProps } = tooltipProps;
  const [span, setSpan] = useState<HTMLSpanElement | null>(null);
  const refCallback = useCallback<RefCallback<HTMLSpanElement>>((node) => setSpan(node), []);
  const [showTooltip, setShowTooltip] = useState(false);
  const measure = useCallback(
    (node: HTMLSpanElement | null) => setShowTooltip(node ? node.offsetWidth < node.scrollWidth : false),
    []
  );
  useLayoutEffect(() => {
    measure(span);
  }, [span, children, measure]);
  useLayoutEffect(() => {
    if (!span || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(() => measure(span));
    observer.observe(span);
    return () => observer.disconnect();
  }, [span, measure]);
  return (
    <DashboardTooltip title={showTooltip ? title : ""} {...restTooltipProps}>
      <Box
        component="span"
        ref={refCallback}
        data-test={dataTest}
        sx={(theme) => ({
          ...theme.mixins.truncateOverflow,
          display: "block",
        })}
      >
        {children}
      </Box>
    </DashboardTooltip>
  );
};
